import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Banner,
  Button,
  Input,
  List,
  Placeholder,
  Section,
  Select,
  Spinner,
  Textarea,
} from "@telegram-apps/telegram-ui";

import { canAccessRequest, getDefaultPath } from "../access";
import { api } from "../api";
import { useAppData } from "../AppData";
import type { VisualType } from "../types";
import { copyToClipboard, countCharsNoSpaces, isYandexDiskLink } from "../utils";

const MIN_TEXT_CHARS = 200;
const VISUAL_TYPES: VisualType[] = ["сетка", "карусель"];

export function RequestPage() {
  const { me, operations, loading: meLoading, error: meError } = useAppData();

  const [contractors, setContractors] = useState<string[] | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const [contractor, setContractor] = useState("");
  const [theme, setTheme] = useState("");
  const [links, setLinks] = useState("");
  const [visualUrl, setVisualUrl] = useState("");
  const [visualType, setVisualType] = useState<VisualType>("сетка");
  const [text, setText] = useState("");

  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [result, setResult] = useState("");
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const hasAccess = canAccessRequest(me);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  useEffect(() => {
    if (!hasAccess) return;
    api
      .getRequestConfig()
      .then((cfg) => {
        setContractors(cfg.contractors);
        setContractor((prev) => prev || cfg.contractors[0] || "");
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : String(e)),
      );
  }, [hasAccess]);

  const charCount = useMemo(() => countCharsNoSpaces(text), [text]);
  const visualValid = isYandexDiskLink(visualUrl);

  const canSubmit =
    !!contractor &&
    theme.trim().length > 0 &&
    links.trim().length > 0 &&
    visualValid &&
    text.trim().length > 0 &&
    !building;

  const submit = async () => {
    if (!canSubmit) return;
    setBuilding(true);
    setBuildError(null);
    setCopied(false);
    try {
      const res = await api.buildRequest({
        contractor,
        theme,
        links,
        visual_url: visualUrl,
        visual_type: visualType,
        text,
      });
      setResult(res.text);
      setUnmatched(res.unmatched_links);
    } catch (e: unknown) {
      setBuildError(e instanceof Error ? e.message : String(e));
    } finally {
      setBuilding(false);
    }
  };

  const copy = async () => {
    const ok = await copyToClipboard(result);
    setCopied(ok);
    if (ok) {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred("success");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (meLoading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  if (meError) {
    return <Placeholder header="Ошибка" description={meError} />;
  }

  if (!hasAccess) {
    return <Navigate to={getDefaultPath(me, operations)} replace />;
  }

  return (
    <div className="page page--with-nav">
      <List>
        <Section header="Заявка">
          <Select
            header="Контрагент"
            value={contractor}
            onChange={(e) => setContractor(e.target.value)}
          >
            {contractors !== null && contractors.length === 0 && (
              <option value="" disabled>
                Список контрагентов пуст
              </option>
            )}
            {contractors === null && (
              <option value="" disabled>
                Загрузка…
              </option>
            )}
            {(contractors ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>

          <Input
            header="Тема"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="Тема заявки"
          />

          <Textarea
            header="Ссылки"
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            placeholder="По одной ссылке в строке"
          />

          <Input
            header="Визуал"
            value={visualUrl}
            onChange={(e) => setVisualUrl(e.target.value)}
            placeholder="https://disk.yandex.ru/d/..."
            status={visualUrl && !visualValid ? "error" : "default"}
          />
          {visualUrl && !visualValid && (
            <div className="muted" style={{ padding: "0 16px 8px" }}>
              Ожидается ссылка на Яндекс Диск (например,
              https://disk.yandex.ru/d/…).
            </div>
          )}

          <Select
            header="Тип визуала"
            value={visualType}
            onChange={(e) => setVisualType(e.target.value as VisualType)}
          >
            {VISUAL_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>

          <Textarea
            header="Текст"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Текст публикации"
          />
          <div
            className="muted"
            style={{
              padding: "0 16px 8px",
              color:
                charCount > 0 && charCount < MIN_TEXT_CHARS
                  ? "var(--tgui--destructive_text_color, #e53935)"
                  : undefined,
            }}
          >
            Знаков: {charCount}
            {charCount > 0 &&
              charCount < MIN_TEXT_CHARS &&
              ` — меньше ${MIN_TEXT_CHARS}`}
          </div>
        </Section>

        {configError && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Ошибка" description={configError} />
          </div>
        )}

        <div style={{ padding: "8px 16px" }}>
          <Button
            size="l"
            stretched
            disabled={!canSubmit}
            loading={building}
            onClick={submit}
          >
            Сформировать
          </Button>
        </div>

        {buildError && (
          <div style={{ padding: "8px 16px" }}>
            <Banner
              type="section"
              header="Не удалось сформировать"
              description={buildError}
            />
          </div>
        )}

        {result && (
          <div ref={resultRef} style={{ scrollMarginTop: 12 }}>
            <Section header="Текст заявки">
              <Textarea
                value={result}
                onChange={(e) => setResult(e.target.value)}
                style={{ minHeight: 280 }}
              />
              {unmatched.length > 0 && (
                <div className="muted" style={{ padding: "8px 16px" }}>
                  Цена не найдена для {unmatched.length} ссыл.
                  {unmatched.length === 1 ? "ки" : "ок"} — они не учтены в сумме.
                </div>
              )}
              <div style={{ padding: "8px 16px" }}>
                <Button size="l" stretched mode="bezeled" onClick={copy}>
                  {copied ? "Скопировано" : "Скопировать"}
                </Button>
              </div>
            </Section>
          </div>
        )}
      </List>
    </div>
  );
}
